/**
 * @file
 * Display a snackbar with a Markdown formatted message. When inserting arbitrary strings such as a ticker symbol or
 * watchlist name, escape the string using [$markdown.escape](#/global/markdown). By default, additional identical
 * notifications will simply prolong the timeout of the already displayed snackbar.
 *
 * @example
 * $notify('Hello, world.');
 * @example
 * $notify('Goodbye, world.', { tone: 'error' });
 * @example
 * $notify('Ok, world.', { tone: 'ok' });
 * @example
 * $notify('I am unique.', { unique: true });
 * @example
 * $notify('I\'m up here.', { position: 'top' });
 * @example
 * $notify('Actions!', {
 *     action: [{
 *         label: 'Retry',
 *         click: () => { $notify('Retry') }
 *     }, {
 *         label: 'Cancel'
 *     }]
 * });
 */


import snackbar from '../components/private/snackbar.vue';


let uniqueKey = 0;


/**
 * @param {string|object} msg - The message to display, with optional Markdown formatting.
 * @param {object} options
 * @param {string} options.position - Dock position (`top`,`bottom`,`bottom-start`,`bottom-end`,etc).
 * @param {number} options.duration - Duration to display snackbar in milliseconds; by default, `5000`.
 * @param {string} options.tone - Semantic color class.
 * @param {string} options.key - A unique key that can be used to suppress additional snackbars with the same key, even
 * if their message is not identical.
 * @param {boolean} options.unique - If `true`, do not suppress additional snackbars even if the message is identical.
 */
function $notify(msg, options = {}) {
    const app = window.__app;
    if (!app) {
        throw new Error('$notify: no scope');
    }
    msg = '' + msg;
    const key = 'notify:' + (options.key == null ? options.unique ? uniqueKey++ : msg : options.key);
    const dock = app.dock;
    const position = options.position || 'bottom';
    const timeout = Date.now() + (options.duration || 5000);
    if (!dock[position]) {
        Vue.set(dock, position, []);
    }
    let entry = dock[position].find(entry => entry.key === key);
    if (!entry) {
        dock[position].push((entry = {
            is: snackbar,
            key
        }));
    }
    Vue.set(entry, 'attrs', {
        msg,
        timeout,
        ...options
    });
}

export default $notify;
